import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, User, Building2 } from 'lucide-react';

interface UserInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (userInfo: UserInfo) => void;
  isLoading?: boolean;
}

export interface UserInfo {
  firstName: string;
  lastName: string;
  companyName: string;
}

const UserInfoModal: React.FC<UserInfoModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false
}) => {
  const [userInfo, setUserInfo] = useState<UserInfo>({
    firstName: '',
    lastName: '',
    companyName: ''
  });

  const [errors, setErrors] = useState<Partial<UserInfo>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<UserInfo> = {};

    if (!userInfo.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!userInfo.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!userInfo.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit({
      firstName: userInfo.firstName.trim(),
      lastName: userInfo.lastName.trim(),
      companyName: userInfo.companyName.trim()
    });
  };

  const handleInputChange = (field: keyof UserInfo, value: string) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
      // Reset form when closing
      setUserInfo({
        firstName: '',
        lastName: '',
        companyName: ''
      });
      setErrors({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-amber-600" />
            Connect Amazon Account
          </DialogTitle>
          <DialogDescription>
            Enter client information to connect an Amazon Advertising account for this client company.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">Client First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Enter client first name"
                value={userInfo.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                disabled={isLoading}
                className={`bg-gray-50 border-gray-300 dark:bg-gray-700 dark:border-gray-600 ${errors.firstName ? 'border-red-500' : ''}`}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Client Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Enter client last name"
                value={userInfo.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                disabled={isLoading}
                className={`bg-gray-50 border-gray-300 dark:bg-gray-700 dark:border-gray-600 ${errors.lastName ? 'border-red-500' : ''}`}
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyName" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Client Company Name
            </Label>
            <Input
              id="companyName"
              type="text"
              placeholder="Enter the client company name"
              value={userInfo.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              disabled={isLoading}
              className={`bg-gray-50 border-gray-300 dark:bg-gray-700 dark:border-gray-600 ${errors.companyName ? 'border-red-500' : ''}`}
            />
            {errors.companyName && (
              <p className="text-sm text-red-500">{errors.companyName}</p>
            )}
            <p className="text-xs text-gray-600">
              This will be used to organize the Amazon reports for this client
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 dark:border-gray-500"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect Amazon Account'
              )}
            </Button>
          </div>
        </form>

        <div className="text-sm text-gray-600 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
          <strong>What happens next:</strong>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li>You'll be redirected to Amazon to authorize access</li>
            <li>Select the Amazon advertising profile for this client</li>
            <li>Reports will automatically sync daily at 2AM EST</li>
            <li>All data will be organized under "{userInfo.companyName || 'the client company'}"</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserInfoModal;